import React, { useEffect, useRef, useState } from 'react'
import ModalComponent from '..'
import { BACKEND_BASE_URL } from '../../../constants'
import useNetworkStatus from '../../../hooks/useNetworkStatus'
import { TerminalProvider } from '../../../redux-toolkits/terminals/terminals.types'
import { POSListenData } from '../../../redux-toolkits/transactions/transactions.type'
import { Button } from '../../button'
import Icon from '../../icon'

export const POSPaymentMethodPOSModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  reference: string
  qrSrc: string
  provider: TerminalProvider
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  reference,
  qrSrc,
  provider,
}) => {
  const [view, setView] = useState<'details' | 'confirm'>('details')

  const eventSourceRef = useRef<EventSource | null>(null)

  // Cleanup function to close any open EventSource
  const cleanup = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }

  const isOnline = useNetworkStatus()

  useEffect(() => {
    if (provider === 'PAYSTACK') {
      setView('details')
    } else {
      setView('confirm')
    }
  }, [provider])

  // Manage SSE connection
  useEffect(() => {
    if (isOnline) {
      if (
        !eventSourceRef.current ||
        eventSourceRef.current.readyState === EventSource.CLOSED
      ) {
        const source = new EventSource(
          `${BACKEND_BASE_URL}v1/transactions/POS-${reference}/listen`,
        )
        eventSourceRef.current = source

        source.addEventListener('message', (event) => {
          try {
            const newData: POSListenData = JSON.parse(event.data)

            if (newData.transaction.status === 'completed') {
              onSuccessCallback()
            }
          } catch (err) {
            console.error('Error parsing SSE event:', err)
          }
        })

        source.onerror = (err) => {
          console.error('POS Transaction SSE error:', err)
          cleanup()
        }
      }
    } else {
      // If offline, cleanup EventSource
      cleanup()
    }

    return () => {
      cleanup()
    }
  }, [isOnline])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback()
        cleanup()
      }}
      title={'Pay with POS Terminal'}
      childrenClassName="md:!max-w-[420px] !gap-0"
    >
      <div className="flex flex-col gap-6">
        <p className="text-sec-black">
          {view === 'details'
            ? `Only available in-store. Show the barcode or payment reference to the
              attendant to pay at the POS.`
            : `We're waiting to confirm your payment. Please hold on.`}
        </p>

        {view === 'details' ? (
          <img
            src={qrSrc}
            alt="paymentQRCode"
            className="block aspect-square size-[300px] mx-auto"
          />
        ) : (
          <div className="flex flex-col gap-4 items-center">
            <Icon
              id="card-pos"
              width={150}
              height={150}
              className="text-black"
            />

            <div className="w-full h-1 bg-gray-200 overflow-hidden rounded">
              <div className="h-full w-1/3 bg-orange animate-line-loader"></div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-sec-black">
          <hr className="flex-1" />
          <span>or</span>
          <hr className="flex-1" />
        </div>

        <div className="text-base flex flex-col gap-1">
          <p className="font-medium">Offline payment reference</p>
          <p className="text-sec-black">{reference}</p>
        </div>

        {view === 'details' ? (
          <div className="flex- flex-col items-center justify-center gap-4">
            <Button
              label="Payment has been made"
              type="button"
              className="mt-2 w-full"
              disabled={false}
              loading={false}
              primary
              onClick={() => {
                setView('confirm')
              }}
            />
          </div>
        ) : undefined}
      </div>
    </ModalComponent>
  )
}
